import { CheckCircle2, Loader2, ShieldCheck, X, XCircle } from "lucide-react";

// Escrita verificada pedida pelo editor (novo dossiê, renovação, prompt,
// sugestão). Corre no Infinitum; o estado vem do servidor, por isso um F5 não
// a cancela.
const MODE_LABELS = {
        new: "Novo dossiê",
        renew: "Renovação",
        research: "Investigação por prompt",
        suggestion: "Sugestão de leitor",
};

const REASON_LABELS = {
        fewer_than_3_proven_facts: "menos de 3 factos provados",
        angle_not_supported: "as fontes não provam o ângulo pedido (menos de 2 factos sobre ele); reformula o pedido ou indica fontes",
        title_claim_not_proven: "o facto em que assenta o título não se prova",
        title_legal_risk_high: "o título tem risco jurídico alto sem reescrita segura",
        no_readable_sources: "nenhuma fonte legível",
        article_empty_after_verification: "o texto ficou vazio depois da verificação",
        writing_output_invalid: "a escrita saiu num formato inválido",
        plan_invalid: "o Infinitum não conseguiu planear a investigação (tenta de novo)",
        premises_not_found: "nenhuma das premissas do pedido foi encontrada nas fontes e ninguém foi mostrado a dizer a narrativa a analisar; indica links concretos (notícias, publicações) no pedido",
};

const elapsed = (from) => {
        const start = new Date(from).getTime();
        if (!start) return "";
        const seconds = Math.max(0, Math.round((Date.now() - start) / 1000));
        return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min`;
};

export const VerifiedJobBanner = ({ job, eta, onOpen, onDiscard, onRetry }) => {
        if (!job) return null;
        const running = ["pending", "running", "queued", "processing"].includes(job.status);
        const ready = job.status === "succeeded" && job.dossier;
        const insufficient = job.status === "succeeded" && !job.dossier;
        const blocked = job.status === "blocked";
        const failed = !running && !ready && !insufficient && !blocked;
        const stage = job.progress && (job.progress.label || job.progress.stage);
        const report = job.report || {};

        return (
                <section className="adm-card mb-6 p-4" data-testid="verified-job-banner" aria-live="polite">
                        <div className="flex items-start gap-3">
                                {running ? (
                                        <Loader2 size={18} className="mt-0.5 animate-spin text-[var(--ai)]" />
                                ) : ready ? (
                                        <CheckCircle2 size={18} className="mt-0.5 text-[var(--truth)]" />
                                ) : (
                                        <XCircle size={18} className="mt-0.5 text-[var(--deception)]" />
                                )}
                                <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                                <ShieldCheck size={14} strokeWidth={1.75} />
                                                <span className="text-sm font-semibold text-[var(--ink)]">
                                                        {MODE_LABELS[job.mode] || "Escrita verificada"}: {job.title}
                                                </span>
                                        </div>
                                        {running && (
                                                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                                                        {stage
                                                                ? `Etapa atual: ${stage}${job.progress?.step && job.progress?.total ? ` (${job.progress.step} de ${job.progress.total})` : ""}. `
                                                                : "O Infinitum está a ler as fontes, fixar os factos, escrever e verificar. "}
                                                        Decorridos {elapsed(job.startedAt)}; costuma levar {eta || "10–12 min"}. Podes continuar a trabalhar ou fazer F5.
                                                </p>
                                        )}
                                        {blocked && (
                                                <p className="mt-1 text-sm text-[var(--deception)]" data-testid="verified-job-blocked">
                                                        O pedido não foi aceite: {job.error || "o Infinitum recusou o pedido."} O pedido não foi feito; volta a pedi-lo quando indicado.
                                                </p>
                                        )}
                                        {ready && (
                                                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                                                        Pronto: {report.facts_proven ?? "?"} factos provados
                                                        {report.dropped_claims ? `, ${report.dropped_claims} afirmações retiradas por falta de prova` : ""}.
                                                        {job.mode === "renew" ? " A renovação só substitui o dossiê se a guardares no editor." : " Revê e guarda no editor."}
                                                </p>
                                        )}
                                        {insufficient && (
                                                <p className="mt-1 text-sm text-[var(--deception)]">
                                                        Não há prova suficiente para escrever este dossiê: {REASON_LABELS[job.reason] || job.reason || "motivo não indicado"}.
                                                </p>
                                        )}
                                        {failed && (
                                                <p className="mt-1 text-sm text-[var(--deception)]">
                                                        {/temporary|tempor[aá]ri/i.test(job.error || "")
                                                                ? "O motor do Infinitum teve uma falha passageira e o pedido não terminou. Não é o tema: basta repetir (o que já estava feito é reaproveitado e gasta menos quota)."
                                                                : `A escrita verificada falhou: ${job.error || job.status}.`}
                                                </p>
                                        )}
                                        {(failed || blocked) && onRetry && (
                                                <button type="button" onClick={onRetry} className="adm-btn mt-3" data-testid="verified-job-retry">
                                                        Repetir pedido
                                                </button>
                                        )}
                                        {ready && (
                                                <button type="button" onClick={onOpen} className="adm-btn adm-btn-primary mt-3">
                                                        {job.mode === "renew" ? "Abrir renovação no editor" : "Abrir dossiê no editor"}
                                                </button>
                                        )}
                                </div>
                                {!running && (
                                        <button type="button" onClick={onDiscard} className="text-[var(--ink-faint)]" aria-label="Fechar aviso da escrita verificada">
                                                <X size={16} />
                                        </button>
                                )}
                        </div>
                </section>
        );
};
